import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, Ruler, Receipt, Wallet, IndianRupee, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import api, { inr } from "../lib/api";

export default function PosCustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [openBill, setOpenBill] = useState(null);

  const load = () => api.get(`/pos/customers/${id}`).then((r) => setC(r.data)).catch(() => toast.error("Customer not found"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const settle = async () => {
    const amt = window.prompt(`Settle how much of the ${inr(c.balance_due)} pending?`, c.balance_due);
    if (amt === null) return;
    const n = parseInt(amt, 10);
    if (!n || n <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/pos/customers/${id}/settle`, { amount: n, mode: "cash" });
      toast.success(`${inr(n)} settled`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Settle failed"); }
  };

  if (!c) return <div className="p-8 text-slate-500" data-testid="pos-customer-profile">Loading…</div>;

  const billed = c.total_spent || 0;
  const pending = c.balance_due || 0;
  const paid = billed - pending;
  const bills = c.bills || [];
  const settlements = c.settlements || [];
  const measures = Object.entries(c.measurements || {}).filter(([, v]) => v);

  const Stat = ({ icon, label, value, tone = "text-slate-100" }) => (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-widest">{icon} {label}</div>
      <p className={`text-xl sm:text-2xl font-semibold font-mono mt-1 ${tone}`}>{value}</p>
    </div>
  );

  const StatusBadge = ({ status }) => {
    const map = {
      paid: ["bg-emerald-500/15 text-emerald-400", <CheckCircle2 key="i" size={12} />, "Paid"],
      partial: ["bg-amber-500/15 text-amber-400", <AlertCircle key="i" size={12} />, "Partial"],
    };
    const [cls, ic, txt] = map[status] || ["bg-slate-700/40 text-slate-300", null, status];
    return <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-medium ${cls}`}>{ic}{txt}</span>;
  };

  return (
    <div className="p-4 sm:p-8" data-testid="pos-customer-profile">
      <button onClick={() => navigate("/pos/customers")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 mb-5" data-testid="profile-back">
        <ArrowLeft size={15} /> All customers
      </button>

      {/* Identity header */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="grid place-items-center h-16 w-16 bg-slate-800 text-amber-400 rounded-full text-2xl font-semibold shrink-0">
          {c.name?.[0]?.toUpperCase()}
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-100 truncate" data-testid="profile-name">{c.name}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 font-mono mt-1">
            <span className="flex items-center gap-1"><Phone size={12} /> {c.phone}</span>
            {c.email && <span className="flex items-center gap-1"><Mail size={12} /> {c.email}</span>}
            <span className="flex items-center gap-1"><Clock size={12} /> {c.visits || 0} visits</span>
          </div>
        </div>
        {pending > 0 && (
          <button onClick={settle} className="ml-auto px-4 py-2.5 rounded bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm" data-testid="profile-settle">
            Settle {inr(pending)}
          </button>
        )}
      </div>

      {/* Account summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat icon={<IndianRupee size={12} />} label="Total billed" value={inr(billed)} />
        <Stat icon={<Wallet size={12} />} label="Paid" value={inr(paid)} tone="text-emerald-400" />
        <Stat icon={<AlertCircle size={12} />} label="Pending" value={inr(pending)} tone={pending > 0 ? "text-red-400" : "text-slate-300"} />
        <Stat icon={<Receipt size={12} />} label="Bills" value={bills.length} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Bills / purchase history */}
        <div className="lg:col-span-2 space-y-3">
          <h2 className="text-sm text-slate-300 uppercase tracking-widest">Purchase history</h2>
          {bills.length === 0 ? (
            <p className="text-slate-600 text-sm bg-slate-900 border border-slate-800 rounded-lg p-8 text-center">No bills yet</p>
          ) : bills.map((b) => (
            <div key={b.id} className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid={`profile-bill-${b.bill_no}`}>
              <button onClick={() => setOpenBill(openBill === b.id ? null : b.id)} className="w-full flex flex-wrap items-center gap-3 p-4 text-left hover:bg-slate-800/50">
                <div className="min-w-0">
                  <p className="font-mono text-amber-400 text-sm">{b.bill_no}</p>
                  <p className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString("en-IN")} · {b.items?.length || 0} items</p>
                </div>
                <div className="ml-auto flex items-center gap-3">
                  <StatusBadge status={b.status} />
                  <div className="text-right">
                    <p className="font-mono text-slate-200 text-sm">{inr(b.total)}</p>
                    {b.balance > 0 && <p className="text-[10px] text-red-400 font-mono">Due {inr(b.balance)}</p>}
                  </div>
                </div>
              </button>
              {openBill === b.id && (
                <div className="px-4 pb-4 pt-1 border-t border-slate-800 space-y-1">
                  {(b.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between text-xs text-slate-400">
                      <span className="truncate pr-2">{it.name}{it.size ? ` · ${it.size}` : ""} × {it.qty}</span>
                      <span className="font-mono shrink-0">{inr((it.unit_price * it.qty) - (it.line_discount || 0))}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs text-slate-300 pt-2 mt-1 border-t border-slate-800/70 font-mono">
                    <span>Paid {inr(b.paid || 0)} · {(b.payments || []).map((p) => p.mode).join(", ") || "—"}</span>
                    <span>Total {inr(b.total)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Side: measurements + payments made */}
        <div className="space-y-6">
          {measures.length > 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
              <h3 className="text-xs text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Ruler size={12} /> Measurements</h3>
              <div className="flex flex-wrap gap-2">
                {measures.map(([k, v]) => (
                  <span key={k} className="text-xs bg-slate-950 border border-slate-800 rounded px-2 py-1 font-mono text-slate-300">{k}: {v}"</span>
                ))}
              </div>
            </div>
          )}

          {c.notes && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
              <h3 className="text-xs text-slate-400 uppercase tracking-widest mb-2">Notes</h3>
              <p className="text-sm text-slate-300 whitespace-pre-wrap">{c.notes}</p>
            </div>
          )}

          <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
            <h3 className="text-xs text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Wallet size={12} /> Payments received</h3>
            {settlements.length === 0 ? (
              <p className="text-slate-600 text-sm">No manual settlements. Payments are recorded per bill.</p>
            ) : (
              <div className="space-y-2">
                {settlements.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">{new Date(s.at).toLocaleDateString("en-IN")} · <span className="uppercase font-mono">{s.mode}</span></span>
                    <span className="font-mono text-emerald-400">{inr(s.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
